// src/pages/AdminDashboard.js

import React, { useEffect, useState } from 'react'
import { Container, Box, CircularProgress } from '@mui/material'

import AppBarHeader from '../components/AppBarHeader'
import SectionOne from '../components/SectionOne'
import SectionTwo from '../components/SectionTwo'
import SectionThree from '../components/SectionThree'

import { supabase } from '../supabaseClient'
import {
  fetchDashboardSummary,
  getRecentPayments,
  fetchDashboardMonthlyStats,
} from '../apiClient'

export default function AdminDashboard() {
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [errorMsg, setErrorMsg] = useState('')
  const [userId, setUserId] = useState(null)
  const [role, setRole] = useState(null) // 'super_admin' | 'admin' | 'user' | null

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true)
      setErrorMsg('')

      try {
        // 1️⃣ Get current user from Supabase Auth
        const { data: userData, error: userErr } = await supabase.auth.getUser()
        if (userErr) {
          console.error('[AdminDashboard] getUser error:', userErr)
        }

        const uid = userData?.user?.id || null
        setUserId(uid)

        // 2️⃣ Get role from approved_users (default to 'user' if not found)
        let resolvedRole = null
        if (uid) {
          try {
            const { data: roleRow, error: roleErr } = await supabase
              .from('approved_users')
              .select('role')
              .eq('user_id', uid)
              .single()

            if (roleErr) {
              console.error('[AdminDashboard] role fetch error:', roleErr)
              resolvedRole = 'user'
            } else {
              resolvedRole = roleRow?.role || 'user'
            }
          } catch (err) {
            console.error('[AdminDashboard] role fetch exception:', err)
            resolvedRole = 'user'
          }
        }
        setRole(resolvedRole)

        // 3️⃣ Fetch all dashboard data from Node backend with (userId, role)
        const [summaryRes, recentRes, monthlyRes] = await Promise.all([
          fetchDashboardSummary(uid, resolvedRole),
          getRecentPayments(uid, resolvedRole),
          fetchDashboardMonthlyStats(uid, resolvedRole),
        ])

        // summaryRes is whatever /api/dashboard/summary returns:
        // {
        //   status: 'ok',
        //   currentMonth,
        //   totalVehicles,
        //   totalCollectedThisMonth,
        //   expectedThisMonth,
        //   profit,
        //   fallingBehindDrivers,
        //   topVehicles
        // }
        const summary = summaryRes || {}
        const recentPayments = recentRes?.payments || []
        const monthlyStats = monthlyRes?.months || []

        // Flatten everything into one object for the Sections
        setDashboardData({
          ...summary,
          recentPayments,
          monthlyStats,
          _meta: {
            userId: uid,
            role: resolvedRole,
          },
        })
      } catch (err) {
        console.error('[AdminDashboard] Failed to load dashboard:', err)
        setErrorMsg('Failed to load dashboard data.')
        setDashboardData(null)
      } finally {
        setLoading(false)
      }
    }

    loadDashboard()
  }, [])

  if (loading) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
        }}
      >
        <CircularProgress size={50} thickness={4} />
      </Box>
    )
  }

  return (
    <>
      <AppBarHeader />

      <Container maxWidth="xl" sx={{ pt: 12, pb: 4 }}>
        <div style={{ fontFamily: 'Arial, sans-serif' }}>
          {errorMsg && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                borderRadius: '8px',
                backgroundColor: '#ffebee',
                color: '#c62828',
                fontSize: 14,
              }}
            >
              {errorMsg}
            </Box>
          )}

          <div
            style={{
              background: '#fff',
              padding: '1.5rem',
              borderRadius: '12px',
              marginBottom: '2rem',
              boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            }}
          >
            {/* 🔹 Summary tiles / top info */}
            <SectionOne data={dashboardData} />
          </div>

          <div
            style={{
              background: '#fff',
              padding: '1.5rem',
              borderRadius: '12px',
              marginBottom: '2rem',
              boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            }}
          >
            {/* 🔹 Recent payments + falling behind */}
            {/* Uses data.recentPayments and data.fallingBehindDrivers */}
            <SectionTwo data={dashboardData} />
          </div>

          <div
            style={{
              background: '#fff',
              padding: '1.5rem',
              borderRadius: '12px',
              marginBottom: '2rem',
              boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            }}
          >
            {/* 🔹 Monthly chart + top vehicles */}
            {/* Uses data.monthlyStats and data.topVehicles */}
            <SectionThree data={dashboardData} />
          </div>
        </div>
      </Container>
    </>
  )
}